import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefCallback,
} from "react";
import { Box, Button, Card, CardContent, Typography } from "@mui/material";
import { CloudUpload } from "@mui/icons-material";
import { CustomSingleSelect } from "../../components/common/inputFields/customSingleSelect";
import { CustomAutoComplete } from "../../components/common/inputFields/customAutoComplete";
import { slugifyVariantInput } from "./dealTemplateApi";
import { apiGetFile } from "../../services/genesisApi/httpsMethods";

type LastUploadDisplay = {
  current_blob_filename: string;
  created_at: string;
  created_by_email: string;
};

export type DealTemplateVersionItem = {
  id: number;
  template_key: string;
  variant_slug?: string;
  version: number;
  blob_path: string;
  original_filename: string;
  notes: string | null;
  created_at: string;
  created_by_email: string;
};

function formatUploadCreatedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatUploadDateOnly(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { dateStyle: "medium" });
}

function formatTimeSinceUpload(iso: string, nowMs: number): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Math.max(0, nowMs - d.getTime());
  const totalMinutes = Math.floor(diffMs / 60_000);
  if (totalMinutes < 1) return "less than a minute";

  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  const parts: string[] = [];
  if (days > 0) {
    parts.push(`${days} day${days === 1 ? "" : "s"}`);
  }
  if (hours > 0) {
    parts.push(`${hours} hour${hours === 1 ? "" : "s"}`);
  }
  if (minutes > 0 || parts.length === 0) {
    parts.push(`${minutes} minute${minutes === 1 ? "" : "s"}`);
  }
  return parts.join(", ");
}

export function formatVersionOptionLabel(
  item: DealTemplateVersionItem,
): string {
  return `${item.original_filename} · v${item.version} · ${formatUploadDateOnly(item.created_at)}`;
}

export type DealTemplateVariantOption = {
  slug: string;
  label: string;
};

export type DealTemplateCardProps = {
  label: string;
  icon: ReactNode;
  versionCount: number;
  accept: string;
  /** Shown in the drop zone (e.g. ".docx" / ".xlsx") — must match server rules */
  supportedFormatLabel: string;
  isUploading: boolean;
  anyUploadInProgress: boolean;
  lastUpload: LastUploadDisplay | null;
  versionItems: DealTemplateVersionItem[];
  selectedVersionId: number | null;
  /** Fired when the user picks a different version; parent typically confirms before applying. */
  onVersionRevertRequest: (versionId: number) => void;
  /** Optional — e.g. for tests or parent ref tracking */
  fileInputRef?: RefCallback<HTMLInputElement>;
  isFileAllowed: (file: File) => boolean;
  onUploadRequest: (file: File) => void;
  /** Base path for template API calls (e.g. `deal-templates/term_sheet_email`). */
  apiBasePath: string;
  /** Optional query params (e.g. ten pager `?variant=`). */
  apiQueryParams?: Record<string, string>;
  /** Stable id for tests and aria (e.g. `ten_pager/default`). */
  cardId: string;
  /** Total cards in the grid — used for column width (default 4). */
  columnCount?: number;
  /** When set, shows a variant/tag selector (e.g. ten pager standard vs dev finance). */
  variantOptions?: DealTemplateVariantOption[];
  selectedVariant?: string;
  selectedVariantLabel?: string;
  onVariantChange?: (variant: { slug: string; label: string }) => void;
};

type VariantAutocompleteOption = DealTemplateVariantOption & { id: number };

export function DealTemplateCard({
  label,
  icon,
  versionCount,
  accept,
  supportedFormatLabel,
  isUploading,
  anyUploadInProgress,
  lastUpload,
  versionItems,
  selectedVersionId,
  onVersionRevertRequest,
  fileInputRef,
  isFileAllowed,
  onUploadRequest,
  apiBasePath,
  apiQueryParams,
  cardId,
  columnCount = 4,
  variantOptions,
  selectedVariant,
  selectedVariantLabel,
  onVariantChange,
}: DealTemplateCardProps) {
  const [dragActive, setDragActive] = useState(false);
  const [stagedFile, setStagedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wasUploadingRef = useRef(isUploading);

  const dropDisabled = anyUploadInProgress;

  const clearStagedFile = useCallback(() => {
    setStagedFile(null);
    setFileError(null);
  }, []);

  const stageFile = useCallback(
    (file: File) => {
      if (!isFileAllowed(file)) {
        setFileError(
          `Only ${supportedFormatLabel} files are allowed. Received: "${file.name}"`,
        );
        setStagedFile(null);
        return;
      }
      setFileError(null);
      setStagedFile(file);
    },
    [isFileAllowed, supportedFormatLabel],
  );

  useEffect(() => {
    if (!lastUpload) return;
    setNowMs(Date.now());
    const id = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, [lastUpload?.created_at]);

  const handleDrag = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (dropDisabled) return;
      if (e.type === "dragenter" || e.type === "dragover") {
        setDragActive(true);
      } else if (e.type === "dragleave") {
        setDragActive(false);
      }
    },
    [dropDisabled],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (dropDisabled) return;
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        stageFile(e.dataTransfer.files[0]);
      }
    },
    [dropDisabled, stageFile],
  );

  const mergedInputRef = useCallback<RefCallback<HTMLInputElement>>(
    (el) => {
      inputRef.current = el;
      fileInputRef?.(el);
    },
    [fileInputRef],
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;
      stageFile(file);
    },
    [stageFile],
  );

  const handleUploadClick = useCallback(() => {
    if (!stagedFile || isUploading) return;
    onUploadRequest(stagedFile);
  }, [stagedFile, isUploading, onUploadRequest]);

  useEffect(() => {
    if (wasUploadingRef.current && !isUploading && stagedFile) {
      clearStagedFile();
    }
    wasUploadingRef.current = isUploading;
  }, [isUploading, stagedFile, clearStagedFile]);

  const openFileDialog = useCallback(() => {
    if (dropDisabled) return;
    inputRef.current?.click();
  }, [dropDisabled]);

  const handleDownloadClick = useCallback(async () => {
    if (!lastUpload) return;
    try {
      const result = await apiGetFile(`${apiBasePath}/download`, apiQueryParams);
      if (result.success && result.data) {
        const url = window.URL.createObjectURL(result.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = lastUpload.current_blob_filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        console.error("Deal template download failed:", result.message);
      }
    } catch (error) {
      console.error("Deal template download error:", error);
    }
  }, [apiBasePath, apiQueryParams, lastUpload]);

  const handleVersionSelect = useCallback(
    (event: { target: { value: unknown } }) => {
      const v = event.target.value;
      const id = typeof v === "string" ? Number(v) : (v as number);
      if (!Number.isFinite(id)) return;
      const effectiveSelectedId =
        selectedVersionId ?? versionItems[0]?.id ?? null;
      if (effectiveSelectedId !== null && id === effectiveSelectedId) return;
      onVersionRevertRequest(id);
    },
    [onVersionRevertRequest, selectedVersionId, versionItems],
  );

  const versionSelectLabelId = `deal-template-version-select-${cardId.replace(/[/\s]+/g, "-")}`;
  const hasVersionDropdown = versionItems.length > 0;

  const versionSelectOptions = useMemo(
    () =>
      versionItems.map((item) => ({
        ...item,
        label: formatVersionOptionLabel(item),
      })),
    [versionItems],
  );

  const variantAutocompleteOptions = useMemo((): VariantAutocompleteOption[] => {
    const seen = new Set<string>();
    const options: VariantAutocompleteOption[] = [];
    let nextId = 1;
    for (const option of variantOptions ?? []) {
      if (seen.has(option.slug)) continue;
      seen.add(option.slug);
      options.push({ ...option, id: nextId++ });
    }
    if (
      selectedVariant &&
      selectedVariantLabel &&
      !seen.has(selectedVariant)
    ) {
      options.push({
        id: nextId,
        slug: selectedVariant,
        label: selectedVariantLabel,
      });
    }
    return options;
  }, [variantOptions, selectedVariant, selectedVariantLabel]);

  const variantAutocompleteValue = useMemo(():
    | VariantAutocompleteOption
    | string
    | null => {
    const match = variantAutocompleteOptions.find(
      (option) => option.slug === selectedVariant,
    );
    if (match) return match;
    if (selectedVariantLabel) return selectedVariantLabel;
    return null;
  }, [variantAutocompleteOptions, selectedVariant, selectedVariantLabel]);

  const handleVariantChange = useCallback(
    (newValue: VariantAutocompleteOption | string | null) => {
      if (!onVariantChange || newValue == null) return;
      if (typeof newValue === "string") {
        const label = newValue.trim();
        if (!label) return;
        onVariantChange({ slug: slugifyVariantInput(label), label });
        return;
      }
      onVariantChange({ slug: newValue.slug, label: newValue.label });
    },
    [onVariantChange],
  );

  return (
    <Card
      data-template-version-count={versionCount}
      elevation={2}
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        flex: `0 0 calc((100% - ${theme.spacing(2)} * ${columnCount - 1}) / ${columnCount})`,
        maxWidth: `calc((100% - ${theme.spacing(2)} * ${columnCount - 1}) / ${columnCount})`,
        minWidth: 0,
        minHeight: 320,
        height: "100%",
        mt: "10px",
        mb: "10px",
        pl: "20px",
        pr: "20px",
      })}
    >
      <input
        ref={mergedInputRef}
        type="file"
        hidden
        accept={accept}
        onChange={handleInputChange}
      />
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "flex-start",
          width: "100%",
          minHeight: 0,
          boxSizing: "border-box",
          py: 3,
          px: 0,
          "&:last-child": { pb: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            width: "100%",
            maxWidth: "100%",
            gap: 2,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 0,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Typography className="text-general-page-subheader">
            {label}
          </Typography>
          <Box
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            sx={{
              border: "2px dashed #ccc",
              borderRadius: 2,
              padding: 2,
              textAlign: "center",
              cursor: dropDisabled ? "not-allowed" : "pointer",
              width: "100%",
              boxSizing: "border-box",
              backgroundColor: dragActive ? "#eef8f5" : "#fafafa",
              opacity: dropDisabled ? 0.6 : 1,
              "&:hover": dropDisabled
                ? undefined
                : {
                    backgroundColor: "#f5f5f5",
                    borderColor: "#1B7A63",
                  },
              transition: "all 0.2s ease",
            }}
            onClick={openFileDialog}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openFileDialog();
              }
            }}
            role="button"
            tabIndex={dropDisabled ? -1 : 0}
            aria-disabled={dropDisabled}
          >
            <CloudUpload
              sx={{
                fontSize: 40,
                color: dragActive ? "#1B7A63" : "rgba(27, 122, 99, 0.45)",
                mb: 0.5,
              }}
            />
            <Typography
              variant="body2"
              sx={{ fontWeight: "bold", mb: 0.25, display: "block" }}
            >
              {isUploading
                ? "Uploading…"
                : stagedFile
                  ? "Replace staged file"
                  : dragActive
                    ? "Drop file here"
                    : "Drag & drop file here"}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              or click to browse
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.75 }}
            >
              Supported format: {supportedFormatLabel}
            </Typography>
          </Box>
          {onVariantChange ? (
            <Box sx={{ width: "100%", maxWidth: "100%", textAlign: "left" }}>
              <CustomAutoComplete
                label="Template variant"
                selectedValue={variantAutocompleteValue}
                options={variantAutocompleteOptions}
                labelKey="label"
                fullWidth
                handleSelectChange={(_event, newValue) =>
                  handleVariantChange(newValue)
                }
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.75 }}
              >
                Choose an existing variant or type a new name to create one on
                upload.
              </Typography>
            </Box>
          ) : null}
          {fileError ? (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", textAlign: "left", width: "100%" }}
            >
              {fileError}
            </Typography>
          ) : null}
          {stagedFile ? (
            <Box
              sx={{
                width: "100%",
                textAlign: "left",
                border: "1px solid #E0E0E0",
                borderRadius: 2,
                p: 1.5,
                backgroundColor: "#fff",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Ready to upload
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", wordBreak: "break-word", mb: 1.5 }}
              >
                {stagedFile.name}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  className="primary-button-solid"
                  onClick={handleUploadClick}
                  disabled={isUploading}
                  sx={{
                    height: "32px",
                    width: "max-content",
                    minWidth: "max-content",
                  }}
                >
                  Upload
                </Button>
                <Button
                  className="primary-button-clear"
                  onClick={clearStagedFile}
                  disabled={isUploading}
                  sx={{
                    height: "32px",
                    width: "max-content",
                    minWidth: "max-content",
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : null}
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            alignSelf: "stretch",
            width: "100%",
          }}
        >
          {lastUpload ? (
            <Box
              sx={{
                flexShrink: 0,
                mt: 0.5,
                textAlign: "left",
                width: "100%",
                marginTop: "20px",
              }}
            >
              <Typography
                component="div"
                sx={{
                  fontSize: "20px",
                  fontWeight: 700,
                  width: "100%",
                }}
              >
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Last File Uploaded:
                </Box>{" "}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "5px",
                  mt: "5px",
                }}
              >
                <Typography
                  variant="caption"
                  component="div"
                  color="text.secondary"
                  sx={{
                    wordBreak: "break-word",
                    lineHeight: 1.45,
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    Time since last upload:
                  </Box>{" "}
                  {formatTimeSinceUpload(lastUpload.created_at, nowMs)}
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  color="text.secondary"
                  sx={{
                    wordBreak: "break-word",
                    lineHeight: 1.45,
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    File name:
                  </Box>{" "}
                  {lastUpload.current_blob_filename}
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  color="text.secondary"
                  sx={{
                    wordBreak: "break-word",
                    lineHeight: 1.45,
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    Created at:
                  </Box>{" "}
                  {formatUploadCreatedAt(lastUpload.created_at)}
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  color="text.secondary"
                  sx={{
                    wordBreak: "break-word",
                    lineHeight: 1.45,
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    Created by:
                  </Box>{" "}
                  {lastUpload.created_by_email}
                </Typography>
              </Box>
            </Box>
          ) : null}
        </Box>
        <Box
          sx={{
            alignSelf: "stretch",
            flexShrink: 0,
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            gap: hasVersionDropdown && lastUpload ? 2 : 0,
            mt: lastUpload || hasVersionDropdown ? 2 : 0,
          }}
        >
          {lastUpload ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                pr: 2,
                pl: 1,
              }}
            >
              <Button
                className="primary-button-solid"
                onClick={() => void handleDownloadClick()}
                sx={{
                  height: "32px",
                  width: "max-content",
                  minWidth: "max-content",
                }}
              >
                Download
              </Button>
            </Box>
          ) : null}
          {hasVersionDropdown ? (
            <Box sx={{ width: "100%", maxWidth: "100%" }}>
              <Typography
                id={versionSelectLabelId}
                variant="body2"
                sx={{
                  display: "block",
                  color: "rgba(0, 0, 0, 0.6)",
                  mb: 0.5,
                }}
              >
                Version history
              </Typography>
              <CustomSingleSelect
                value={selectedVersionId ?? versionItems[0]!.id}
                onChange={handleVersionSelect}
                options={versionSelectOptions}
                labelKey="label"
                fullWidth
              />
            </Box>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
}
